import * as React from "react";
import type { LucideProps } from "lucide-react";

const PULSE_STYLE_ID = "sent-action-pulse-keyframes";

interface SentActionIndicatorProps {
  Icon: React.ComponentType<LucideProps>;
  label: string;
  /** Colour shared by the icon and the label. */
  accent: string;
}

/**
 * What a user bubble shows for a message an add-in action sent.
 *
 * The message itself was written for the model and is still sent and still
 * sits in the conversation history. The person pressed a button, though, so
 * the bubble reads as the action they took rather than as a sentence they did
 * not write. One component for every such action, so they all read alike.
 */
export function SentActionIndicator({ Icon, label, accent }: SentActionIndicatorProps) {
  React.useEffect(() => {
    if (document.getElementById(PULSE_STYLE_ID)) return;
    const style = document.createElement("style");
    style.id = PULSE_STYLE_ID;
    style.textContent = `
      @keyframes sentActionPulse {
        0%, 100% { opacity: 0.45; transform: scale(0.9); }
        50% { opacity: 1; transform: scale(1.08); }
      }
    `;
    document.head.appendChild(style);
  }, []);

  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <span
        style={{
          display: "inline-flex",
          animation: "sentActionPulse 2s ease-in-out infinite",
          transformOrigin: "center",
        }}
      >
        <Icon size={16} color={accent} strokeWidth={2} />
      </span>
      <span style={{ fontSize: 13, fontWeight: 500, color: accent }}>{label}</span>
    </div>
  );
}
